import { useCallback } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'
import { LogoGradient } from '@carouselgg/nexus/brand'
import { AppBar } from '@carouselgg/nexus/app-bar'
import { PanelLeftClose, PanelLeftOpen } from '@carouselgg/nexus/icon'
import { NotificationBell, NotificationCenter } from '@carouselgg/nexus/notification-center'
import { toast } from '@carouselgg/nexus/toast'
import { AppIndexSearch } from '@carouselgg/auth/app-index-search'
import { useAuthStore } from '@carouselgg/auth/store'
import { useAccountMenu } from '@carouselgg/auth/account-menu'
import { useAppNotifications } from '../context/NotificationContext'
import { ActionLink } from './ActionLink'
import styles from './AppNav.module.css'

interface AppNavProps {
  /** Opens the sidebar as an overlay drawer. Phone widths only. */
  onMenuToggle: () => void
  /** Whether the docked sidebar is currently collapsed to a rail. */
  collapsed?: boolean
  /** Collapses/expands the docked sidebar. Desktop widths only. */
  onCollapseToggle?: () => void
}

export function AppNav({ onMenuToggle, collapsed, onCollapseToggle }: AppNavProps) {
  const navigate = useNavigate()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const location = useLocation()
  const back = encodeURIComponent(location.pathname + location.search)
  // The one account menu, the same on every surface (see useAccountMenu).
  const accountMenu = useAccountMenu()

  const {
    notifications,
    unreadCount,
    isOpen,
    toggle,
    close,
    markRead,
    markAllRead,
    dismiss,
    undoDismiss,
    hasMore,
    isLoadingMore,
    loadMore,
  } = useAppNotifications()

  const handleNotificationClick = useCallback((n: { id: string }) => {
    navigate(`/notifications/${n.id}`)
    close()
  }, [navigate, close])

  const handleDismiss = useCallback((id: string) => {
    dismiss(id)
    toast('Notification deleted', {
      action: { label: 'Undo', onClick: () => undoDismiss(id) },
      duration: 5000,
    })
  }, [dismiss, undoDismiss])

  return (
    <>
      <AppBar
        onMenuToggle={onMenuToggle}
        leading={onCollapseToggle && (
          <button
            type="button"
            className={styles.collapseToggle}
            onClick={onCollapseToggle}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-pressed={!collapsed}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {/* A panel icon depicts the thing being toggled. The old control was
                a bare chevron, which could equally have meant "back". */}
            {collapsed ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={20} />}
          </button>
        )}
        brand={
          // This nav only renders inside the product shell, so the logo goes to
          // the dashboard. The marketing surface has its own nav whose logo
          // goes to `/`.
          <Link to="/dashboard" className={styles.brand}>
            <img src={LogoGradient} alt="" className={styles.logo} />
            <span className={styles.wordmark}>Carousel</span>
          </Link>
        }
        renderSearch={(search) => <AppIndexSearch {...search} />}
        searchPlaceholder="Search"
        // Signed out, Sign up is the one gradient action in the bar; Compete
        // is a side-nav place either way.
        competeUrl={isAuthenticated ? '/compete' : undefined}
        {...accountMenu}
        actions={isAuthenticated ? (
          <NotificationBell
            unreadCount={unreadCount}
            open={isOpen}
            onToggle={toggle}
            onClose={close}
          >
            <NotificationCenter
              notifications={notifications}
              unreadCount={unreadCount}
              onMarkRead={markRead}
              onMarkAllRead={markAllRead}
              onDismiss={handleDismiss}
              onSwipeDismiss={handleDismiss}
              onNotificationClick={handleNotificationClick}
              onAction={(_, to) => { navigate(to); close() }}
              onViewAll={() => { navigate('/notifications'); close() }}
              onSettings={() => { navigate('/settings/notifications'); close() }}
              hasMore={hasMore}
              isLoadingMore={isLoadingMore}
              onLoadMore={loadMore}
            />
          </NotificationBell>
        ) : (
          // The marketing nav says Log in / Sign up plainly; inside the app the
          // only way in used to be the round account icon. Both come back to
          // the page you were on.
          <div className={styles.authActions}>
            <Link to={`/login?redirect=${back}`} className={styles.login}>Log in</Link>
            <ActionLink to={`/signup?redirect=${back}`} className={styles.signup}>Sign up</ActionLink>
          </div>
        )}
      />
    </>
  )
}
